import { useState } from 'react';
import { FormGroup } from '../../components/molecules/FormGroup/FormGroup';
import { formatPhoneInput, isPhoneComplete, isEmailValid } from '../../lib/validators';
import styles from './Inquiry.module.css';

const ORG_TYPE_OPTIONS = [
  { value: '', label: '선택' },
  { value: 'MEDICAL_ASSOCIATION', label: '의사회 (시·도·구)' },
  { value: 'DENTAL_ASSOCIATION', label: '치과의사회' },
  { value: 'ORIENTAL_ASSOCIATION', label: '한의사회' },
  { value: 'ACADEMIC_SOCIETY', label: '전문학회·연구회' },
  { value: 'CLINIC_NETWORK', label: '네트워크 의원 본사' },
  { value: 'MEDICAL_COOP', label: '의료협동조합' },
  { value: 'OTHER', label: '기타 의료인 단체' },
];

const MEMBER_COUNT_OPTIONS = [
  { value: '', label: '선택', count: undefined },
  { value: 'LT_10', label: '10개 미만', count: 0 },
  { value: '10_30', label: '10~30개', count: 10 },
  { value: '30_100', label: '30~100개', count: 30 },
  { value: '100_300', label: '100~300개', count: 100 },
  { value: 'GTE_300', label: '300개 이상', count: 300 },
];

const INTEREST_OPTIONS = [
  { value: '', label: '선택' },
  { value: 'EMERGENCY_LEGAL', label: '회원 긴급 법률상담 지원' },
  { value: 'MEDICAL_INCIDENT', label: '회원 의료사고 전문상담' },
  { value: 'EDUCATION', label: '단체 법률 세미나·교육' },
  { value: 'ASSOCIATION_ADVISORY', label: '협회 차원의 법률 자문' },
  { value: 'FULL_PACKAGE', label: '전체 패키지 (일괄 제공)' },
];

export function GroupForm({ onSubmit, isSubmitting = false }) {
  const [form, setForm] = useState({
    applicantName: '',
    phone: '',
    orgName: '',
    orgType: '',
    memberCountRange: '',
    email: '',
    interest: '',
    message: '',
  });

  const [touched, setTouched] = useState({ phone: false, email: false });

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const updatePhone = (e) =>
    setForm((prev) => ({ ...prev, phone: formatPhoneInput(e.target.value) }));
  const markTouched = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  const phoneError = touched.phone && form.phone && !isPhoneComplete(form.phone);
  const emailError = touched.email && form.email && !isEmailValid(form.email);
  const canSubmit =
    !isSubmitting &&
    form.applicantName.trim() &&
    isPhoneComplete(form.phone) &&
    form.orgName.trim() &&
    isEmailValid(form.email);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const memberCount = MEMBER_COUNT_OPTIONS.find((o) => o.value === form.memberCountRange)?.count;
    onSubmit({
      inquiryType: 'GROUP',
      applicantName: form.applicantName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      message: form.message.trim() || undefined,
      group: {
        orgName: form.orgName.trim(),
        orgType: form.orgType || undefined,
        memberCount,
        interests: form.interest ? [form.interest] : undefined,
      },
    });
  };

  return (
    <div>
      <div className={styles.fRow}>
        <FormGroup label="담당자 성함" required>
          <input
            type="text"
            className={styles.fInput}
            placeholder="홍길동"
            value={form.applicantName}
            onChange={update('applicantName')}
          />
        </FormGroup>
        <FormGroup label="연락처" required>
          <input
            type="tel"
            inputMode="numeric"
            className={styles.fInput}
            placeholder="010-0000-0000"
            value={form.phone}
            onChange={updatePhone}
            onBlur={markTouched('phone')}
            maxLength={13}
          />
          {phoneError && (
            <p className={styles.fHint}>
              전화번호 형식이 올바르지 않습니다. (예: 010-1234-5678, 02-123-4567)
            </p>
          )}
        </FormGroup>
      </div>
      <FormGroup label="단체·기관명" required>
        <input
          type="text"
          className={styles.fInput}
          placeholder="○○의사회 / ○○학회 / ○○네트워크"
          value={form.orgName}
          onChange={update('orgName')}
        />
      </FormGroup>
      <div className={styles.fRow}>
        <FormGroup label="단체 유형" required>
          <select className={styles.fSelect} value={form.orgType} onChange={update('orgType')}>
            {ORG_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormGroup>
        <FormGroup label="회원(가맹) 의원 수" required>
          <select
            className={styles.fSelect}
            value={form.memberCountRange}
            onChange={update('memberCountRange')}
          >
            {MEMBER_COUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormGroup>
      </div>
      <FormGroup label="이메일" required>
        <input
          type="email"
          className={styles.fInput}
          placeholder="contact@association.or.kr"
          value={form.email}
          onChange={update('email')}
          onBlur={markTouched('email')}
        />
        {emailError && <p className={styles.fHint}>올바른 이메일 형식이 아닙니다.</p>}
      </FormGroup>
      <FormGroup label="주요 관심 서비스">
        <select className={styles.fSelect} value={form.interest} onChange={update('interest')}>
          {INTEREST_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FormGroup>
      <FormGroup label="문의사항">
        <textarea
          className={styles.fTextarea}
          placeholder="단체의 현황이나 필요하신 서비스를 자유롭게 기재해 주세요."
          value={form.message}
          onChange={update('message')}
        />
      </FormGroup>
      <button
        type="button"
        className={`${styles.fSubmit} ${styles.btnOutline}`}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {isSubmitting ? '제출 중...' : '문의 제출하기'}
      </button>
      <p className={styles.fNotice}>
        제출하신 정보는 상담 목적으로만 활용되며, 외부에 제공되지 않습니다.
      </p>
    </div>
  );
}
